import styled, { keyframes, css } from "styled-components";
import { Link } from "react-router-dom";

/* ---------- Animations ---------- */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const popFromCenter = keyframes`
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

export const AnimatedProjectStep = css`
  opacity: 0;
  transform: scale(0.9);
  transition: opacity 0.6s ease, transform 0.6s ease;

  &.in-view {
    opacity: 1;
    animation: ${popFromCenter} 0.8s ease forwards;
  }
`;

/* ---------- Main Container ---------- */
export const MainContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

/* ---------- Guest Section ---------- */
export const GuestSection = styled.section`
  padding: 10px 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 600px) {
    padding: 8px 10px;
  }
`;

/* ---------- Guest Box ---------- */
export const GuestBox = styled.div`
  border-radius: 17px;
  width: 100%;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  background: #fff;
  padding: 15px;

  @media (max-width: 480px) {
    width: 110%;
    padding: 10px;
  }
`;

export const GuestContent = styled.div`
  animation: ${fadeIn} 0.4s ease;
  opacity: ${({ $show }) => ($show ? 1 : 0.8)};
  transition: opacity 0.3s ease;
`;

/* ---------- Authenticated Nav ---------- */
export const AuthenticatedToolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
  }
`;

export const StyledNavWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  max-width: 100%;
  padding: 0;
  overflow: hidden;

  @media (max-width: 960px) {
    padding: 0 42px;
  }
`;

export const IconButtonLeft = styled.button`
  position: absolute;
  left: 0;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--app-nav-active-bg, #f4f6f8);
  border: 1px solid var(--app-border, #626a73);
  border-radius: 999px;
  padding: 8px;
  cursor: pointer;
  color: var(--app-nav-active-text, #202124);
  font-size: 18px;

  &:hover {
    color: var(--app-nav-active-text, #202124);
    transform: scale(1.1);
  }

  @media (min-width: 961px) {
    display: none;
  }
`;

export const IconButtonRight = styled.button`
  position: absolute;
  right: 0;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--app-nav-active-bg, #f4f6f8);
  border: 1px solid var(--app-border, #626a73);
  border-radius: 999px;
  padding: 8px;
  cursor: pointer;
  color: var(--app-nav-active-text, #202124);
  font-size: 18px;

  &:hover {
    color: var(--app-nav-active-text, #202124);
    transform: scale(1.1);
  }

  @media (min-width: 961px) {
    display: none;
  }
`;

export const StyledNav = styled.nav`
  display: flex;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding: 10px 4px;
  flex: 1;
  overflow-x: hidden;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 960px) {
    gap: 10px;
    overflow-x: auto;
  }
`;

export const StyledLink = styled(Link)`
  flex: 1 1 0;
  color: var(--app-text, #f4f6f8);
  text-decoration: none;
  font-size: 14px;
  font-weight: bold;
  min-height: 38px;
  padding: 8px 10px;
  border-radius: 8px;
  text-align: center;
  white-space: normal;
  transition: all 0.2s ease-in-out;

  @media (max-width: 960px) {
    flex: 0 0 auto;
    padding: 8px 14px;
    white-space: nowrap;
  }

  &:hover {
    background-color: var(--app-nav-hover, #4a5058);
  }

  &.active {
    background-color: var(--app-nav-active-bg, #f4f6f8);
    color: var(--app-nav-active-text, #202124);
  }
`;

export const ThemeToggleButton = styled.button`
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid var(--app-border, #626a73);
  border-radius: 10px;
  padding: 0;
  background: var(--app-nav-surface, #3b3f45);
  color: var(--app-theme-toggle-icon, #334155);
  font-size: 21px;
  cursor: pointer;
  transition:
    background 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: var(--app-nav-hover, #4a5058);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid var(--app-theme-toggle-icon, #334155);
    outline-offset: 3px;
  }
`;

/* ---------- Centered Button ---------- */
export const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

export const HistoryButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${({ $variant }) => ($variant === "secondary" ? "#ffffff" : "#242424")};
  color: ${({ $variant }) => ($variant === "secondary" ? "#242424" : "#ffffff")};
  font-size: 16px;
  font-weight: bold;
  border: 1px solid ${({ $variant }) => ($variant === "secondary" ? "#bdbdbd" : "#242424")};
  border-radius: 8px;
  cursor: pointer;
  transition: 0.3s ease;

  &:hover {
    background: ${({ $variant }) => ($variant === "secondary" ? "#f0f0f0" : "#3a3a3a")};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(1px);
  }

  [data-auth-theme="night"] & {
    background: ${({ $variant }) => $variant === "secondary" ? "var(--app-surface)" : "var(--app-nav-active-bg)"};
    color: ${({ $variant }) => $variant === "secondary" ? "var(--app-surface-text)" : "var(--app-nav-active-text)"};
    border-color: var(--app-border);

    &:hover {
      background: var(--app-surface-hover);
    }
  }
`;

/* ---------- Project Timeline Layout ---------- */
export const ProjectContainer = styled.div`
  position: relative;
  width: 40%;
  margin: 10px auto;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    width: 90%;
  }
`;

export const ProjectLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 2px;
  background: repeating-linear-gradient(
    to bottom,
    #29b6f6,
    #29b6f6 6px,
    transparent 6px,
    transparent 12px
  );
  transform: translateX(-50%);
  z-index: 0;

  @media (max-width: 768px) {
    left: 25px;
    transform: none;
  }
`;

export const ProjectStep = styled.div`
  display: grid;
  grid-template-columns: 1fr 60px 1fr;
  align-items: center;
  width: 100%;
  margin: 60px 0;
  position: relative;
  ${AnimatedProjectStep};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

export const StepCircle = styled.div`
  width: 44px;
  height: 44px;
  background-color: #29b6f6;
  color: white;
  font-weight: bold;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  z-index: 2;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.25);
`;

export const StepButton = styled.button`
  background: ${({ $isActive }) =>
    $isActive
      ? "linear-gradient(135deg, #1e1e1e, #333)"
      : "linear-gradient(135deg, #2a2a2a, #1a1a1a)"};
  color: ${({ $isActive }) => ($isActive ? "#fff" : "#e0e0e0")};
  font-size: 16px;
  font-weight: 600;
  padding: 18px 26px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
  width: 220px;
  text-align: center;

  &:hover {
    background: linear-gradient(135deg, #3a3a3a, #2a2a2a);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    justify-self: center;
  }
`;

export const StepDescription = styled.div`
  background: #f9f9fb;
  padding: 22px;
  border-radius: 12px;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.4s ease;

  h3 {
    color: #29b6f6;
    margin-bottom: 10px;
  }

  p {
    color: #333;
    line-height: 1.5;
  }

  @media (max-width: 768px) {
    justify-self: center;
    width: 85%;
  }
`;

